import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Reveal } from "../components/Reveal";
import { fetchServices } from "../lib/api";
import { ArrowUpRight, Check } from "lucide-react";

const Services = () => {
  const [services, setServices] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    fetchServices().then((s) => {
      setServices(s);
      // jump to anchor if present
      const hash = window.location.hash.replace("#", "");
      if (hash) {
        setActive(hash);
        setTimeout(() => {
          document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 200);
      } else if (s[0]) setActive(s[0].slug);
    });
  }, []);

  useEffect(() => {
    const onScroll = () => {
      for (const s of services) {
        const el = document.getElementById(s.slug);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top <= 200 && rect.bottom > 200) { setActive(s.slug); return; }
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [services]);

  return (
    <div data-testid="services-page">
      {/* Page header */}
      <section className="border-b border-[var(--rule)] py-20 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-3">
            <p className="text-[11px] tracking-[0.32em] uppercase text-neutral-500">— Vol. 02 · Practice</p>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-9">
            <h1 className="font-serif text-5xl sm:text-7xl lg:text-[8.5vw] font-light tracking-tighter leading-[0.92]">
              The work, in <span className="italic">four</span> parts.
            </h1>
            <p className="font-serif text-xl lg:text-2xl text-neutral-700 mt-10 max-w-3xl leading-snug">
              Litigation, consulting, assurance and finance advisory — each practised by partners
              who have spent the better part of their careers in the field. The detail below is
              what we actually do, day to day.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Body with sticky TOC */}
      <section className="py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* TOC */}
          <aside className="lg:col-span-3 lg:sticky lg:top-32 lg:self-start" data-testid="services-toc">
            <p className="text-[11px] tracking-[0.32em] uppercase text-neutral-500 mb-5">Index</p>
            <nav className="flex flex-col border-t border-neutral-900">
              {services.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.slug}`}
                  data-testid={`toc-${s.slug}`}
                  onClick={() => setActive(s.slug)}
                  className={`py-4 border-b border-[var(--rule)] flex items-baseline gap-4 transition-colors ${
                    active === s.slug ? "text-neutral-950" : "text-neutral-500 hover:text-neutral-950"
                  }`}
                >
                  <span className="marker text-xl">{s.number}</span>
                  <span className="font-serif text-2xl">{s.title}</span>
                </a>
              ))}
            </nav>
          </aside>

          {/* Detail */}
          <div className="lg:col-span-9 space-y-32">
            {services.map((s) => (
              <article key={s.id} id={s.slug} data-testid={`service-detail-${s.slug}`} className="scroll-mt-32">
                <Reveal>
                  <div className="flex items-baseline gap-6 mb-6 border-t border-neutral-900 pt-8">
                    <span className="marker text-3xl text-neutral-500">{s.number}</span>
                    <span className="text-[11px] tracking-[0.32em] uppercase text-neutral-500">— Discipline</span>
                  </div>
                  <h2 className="font-serif text-5xl lg:text-7xl font-light tracking-tighter leading-none">
                    {s.title}
                  </h2>
                  <p className="font-serif text-xl lg:text-2xl text-neutral-700 mt-8 max-w-3xl leading-snug">
                    {s.summary}
                  </p>
                  <p className="text-base text-neutral-700 mt-10 max-w-3xl leading-relaxed">{s.detail}</p>
                </Reveal>

                <Reveal delay={0.15}>
                  <div className="mt-12 border-t border-[var(--rule)]">
                    <p className="text-[11px] tracking-[0.32em] uppercase text-neutral-500 mt-8 mb-6">— What we do</p>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10">
                      {s.offerings.map((o, i) => (
                        <li
                          key={i}
                          className="flex gap-4 py-4 border-b border-[var(--rule)] text-base text-neutral-800 leading-relaxed"
                        >
                          <Check size={16} strokeWidth={1.5} className="mt-1 shrink-0 text-neutral-400" />
                          <span>{o}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              </article>
            ))}

            <Reveal>
              <div className="border-t border-neutral-900 pt-10">
                <Link to="/contact" data-testid="services-contact-cta" className="btn-editorial">
                  Begin a conversation <ArrowUpRight size={14} />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
